import { Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TrackClickDirective } from '../directives/track-click.directive';
import { LEGAL, PageKey, USE_CASES, routerCommands, toLang } from '../seo/seo.config';
import { ConsentService } from '../services/consent.service';
import { FeaturesService } from '../services/features.service';
import { LanguageService } from '../services/language.service';
import { IconComponent } from './icon.component';

@Component({
  selector: 'app-footer',
  imports: [RouterLink, TranslateModule, IconComponent, TrackClickDirective],
  template: `
    <footer class="footer" data-screen-label="Footer">
      <div class="wrap">
        <div class="foot-top">
          <div>
            <img
              src="/assets/logo-white.webp"
              class="logo"
              alt="Chattoo"
              width="480"
              height="129"
              loading="lazy"
              decoding="async"
            />
            <p class="blurb">{{ 'footer.blurb' | translate }}</p>
          </div>
          <div class="foot-col">
            <h2>{{ 'footer.product' | translate }}</h2>
            <a [routerLink]="link('channels')">{{ 'nav.channels' | translate }}</a>
            <a [routerLink]="link('features')">{{ 'nav.features' | translate }}</a>
            <a [routerLink]="link('howItWorks')">{{ 'nav.howItWorks' | translate }}</a>
            <a [routerLink]="link('pricing')">{{ 'nav.pricing' | translate }}</a>
            <a [routerLink]="link('sign')">{{ 'nav.sign' | translate }}</a>
            <a [routerLink]="link('voice')">{{ 'footer.voice' | translate }}</a>
          </div>
          <div class="foot-col">
            <h2>{{ 'footer.company' | translate }}</h2>
            <a [routerLink]="link('about')">{{ 'footer.aboutUs' | translate }}</a>
            <a [routerLink]="link('blog')">{{ 'nav.blog' | translate }}</a>
            <a [routerLink]="link('contacts')" [appTrackClick]="'Contact'">{{ 'nav.contacts' | translate }}</a>
          </div>
          <div class="foot-col">
            <h2>{{ 'footer.legal' | translate }}</h2>
            <a [routerLink]="link('privacy')">{{ 'footer.privacy' | translate }}</a>
            <a [routerLink]="link('terms')">{{ 'footer.terms' | translate }}</a>
            <!-- L'ancora porta la lingua del documento: #en-cookie apre l'informativa in inglese. -->
            <a [routerLink]="link('privacy')" [fragment]="cookieAnchor">{{ 'footer.cookie' | translate }}</a>
            <!-- Brings the cookie banner back: the choice can be changed at any time (D12). -->
            <button type="button" class="cookie-prefs" (click)="consent.open()">
              {{ 'footer.cookiePrefs' | translate }}
            </button>
            @if (features.deleteAccountPage) {
              <a [routerLink]="link('deleteAccount')">{{ 'footer.deleteAccount' | translate }}</a>
            }
          </div>
          <!-- The nine use-case sheets: the deepest URLs in the site, one crawl hop from every
               page. Same list as the index and the home grid (USE_CASES). -->
          <div class="foot-col foot-sectors">
            <h2>{{ 'nav.useCases' | translate }}</h2>
            @for (u of useCases; track u.id) {
              <a [routerLink]="link(u.page, u.params)">{{ 'useCases.' + u.id + '.nav' | translate }}</a>
            }
          </div>
        </div>
        <!-- Dati societari obbligatori. NESSUN recapito: telefono ed email stanno solo
             nella pagina Contatti, e il numero fisso non si pubblica. -->
        <address class="foot-legal">
          <strong>{{ legal.company }}</strong>
          <span>{{ legal.street }}, {{ legal.postalCode }} {{ legal.city }} ({{ legal.province }})</span>
          <span>{{ 'footer.vat' | translate }} {{ legal.vat }}</span>
        </address>
        <div class="foot-bot">
          <span>{{ 'footer.copy' | translate }}</span>
          <div class="foot-social">
            <a href="https://www.instagram.com/chattoo.it/" target="_blank" rel="noopener" aria-label="Instagram"><i data-lucide="instagram"></i></a>
            <a href="https://www.facebook.com/profile.php?id=61587523014524" target="_blank" rel="noopener" aria-label="Facebook"><i data-lucide="facebook"></i></a>
          </div>
        </div>
      </div>
    </footer>
  `,
})
export class FooterComponent {
  protected readonly useCases = USE_CASES;
  protected readonly legal = LEGAL;

  constructor(
    private readonly lang: LanguageService,
    protected readonly features: FeaturesService,
    protected readonly consent: ConsentService,
  ) {}

  get currentLang(): string {
    return this.lang.current;
  }

  /** RouterLink target for a page in the current language, using its canonical slug. */
  protected link(page: PageKey, params?: Readonly<Record<string, string>>): string[] {
    return routerCommands(page, toLang(this.lang.current), params);
  }

  /** The cookie section of the privacy page, in the site's language (`it-cookie`, `en-cookie`). */
  protected get cookieAnchor(): string {
    return `${toLang(this.lang.current)}-cookie`;
  }
}
